import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class ItemAtLocation extends ModelObjectWithDirtyCheck<ItemAtLocation> {

    /**
    * Stock Item Id
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Stock Location Id
    */
    public stockLocationId: Guid = Guid.createEmpty();
    private _reorderLevel: number | null = null;
    /**
    * The level at which users should re-order stock.
    * Not Negative
    */
    public get reorderLevel(): number | null { return this._reorderLevel }
    public set reorderLevel(value: number | null) {
      this._reorderLevel = value === undefined ? null : value;
    }
    private _reorderQuantity: number | null = null;
    /**
    * The default amount that the user should order.
    * Not Negative
    */
    public get reorderQuantity(): number | null { return this._reorderQuantity }
    public set reorderQuantity(value: number | null) {
      this._reorderQuantity = value === undefined ? null : value;
    }
    private _stockLevelAlert: number | null = null;
    /**
    * The level at which the user should be notified that more stock is required.
    * Not Negative
    */
    public get stockLevelAlert(): number | null { return this._stockLevelAlert }
    public set stockLevelAlert(value: number | null) {
      this._stockLevelAlert = value === undefined ? null : value;
    }
    /**
    * Whether this item should be offered for sale from this location (applicable to sales-type items).
    */
    public allowSales: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.reorderLevel = jsonSerializedObject.reorderLevel == null ? null : jsonSerializedObject.reorderLevel;
          this.reorderQuantity = jsonSerializedObject.reorderQuantity == null ? null : jsonSerializedObject.reorderQuantity;
          this.stockLevelAlert = jsonSerializedObject.stockLevelAlert == null ? null : jsonSerializedObject.stockLevelAlert;
          this.allowSales = jsonSerializedObject.allowSales;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.reorderLevel = this.reorderLevel == null ? null : this.reorderLevel;
        toRet.reorderQuantity = this.reorderQuantity == null ? null : this.reorderQuantity;
        toRet.stockLevelAlert = this.stockLevelAlert == null ? null : this.stockLevelAlert;
        toRet.allowSales = this.allowSales;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "ItemAtLocation.StockItemId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "ItemAtLocation.StockLocationId cannot be empty"},
    ];
}


export class ItemAtLocationService extends ServiceBase {

  public async saveItemAtLocation(itemAtLocation: ItemAtLocation, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    itemAtLocation:itemAtLocation.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("itemAtLocation/v1/saveItemAtLocation", req, true, extractor, opts);
  };

  public async getItemAtLocations(limitItemIds: readonly Guid[] | null, limitStockLocationIds: readonly Guid[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(ItemAtLocation)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    limitItemIds:limitItemIds == null ? null : limitItemIds.map(v => v.toString()),
    limitStockLocationIds:limitStockLocationIds == null ? null : limitStockLocationIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.itemAtLocations.map((v: any) => new ItemAtLocation(v));;
    return this.makeJsonRequest("itemAtLocation/v1/getItemAtLocations", req, false, extractor, opts);
  };
}

export const itemAtLocationService = new ItemAtLocationService();